import React, { memo, ReactNode } from 'react'
import { Argument as ClassName } from 'classnames'
import { Controller, useForm } from 'react-hook-form'
import { Button } from 'primereact/button'

import { isAdminDomain, isSupportDomain } from '../../../../lib/currentDomain'
import { MeasurementDevicesGetFilteredApiArg } from '../../../redux/api/web-api'
import useText from '../../../hooks/useText'
import { getProductName } from '../../../services/getProductName'
import { Input } from '../../../uikit/Input'
import { Dropdown } from '../../../uikit/Dropdown'

export type MeasurementDeviceSearchListFormProps = {
    className?: ClassName
    clearDevice: () => void
    onSubmit: (filters: MeasurementDevicesGetFilteredApiArg) => void
    style?: React.CSSProperties
    isLoading: boolean
    actions?: ReactNode
}

export const MeasurementDeviceSearchListForm = memo(function MeasurementDeviceSearchListForm({
    clearDevice,
    onSubmit,
    isLoading,
    style,
    actions,
}: MeasurementDeviceSearchListFormProps): React.ReactElement {
    const text = useText()

    const { register, handleSubmit, reset, control } = useForm<MeasurementDevicesGetFilteredApiArg>()

    const statuses = [
        {
            value: text('lblOffline'),
            label: text('lblOffline'),
        },
        {
            value: text('lblOnline'),
            label: text('lblOnline'),
        },
    ]

    const measurementDeviceProducts = [
        {
            value: 5,
            label: getProductName(5),
        },
        {
            value: 8,
            label: getProductName(8),
        },
        {
            value: 6,
            label: getProductName(6),
        },
    ]

    return (
        <form name="form" role="form" style={style} noValidate onSubmit={handleSubmit(onSubmit)} className="mb-4">
            <div className="flex flex-wrap flex-column gap-4">
                <div className="flex gap-4">
                    <div className="flex-1">
                        <Input
                            placeholder={text('lblSerialNumber')}
                            data-testid="input-serial"
                            id="deviceSerialNumber"
                            key="device SerialNumber"
                            {...register('serialNumber')}
                        />
                    </div>

                    <div className="flex-1">
                        <Input
                            placeholder={text('lblName')}
                            data-testid="input-name"
                            id="deviceName"
                            key="device Name"
                            {...register('name')}
                        />
                    </div>

                    {!isAdminDomain && (
                        <>
                            <div className="flex-1">
                                <Input
                                    placeholder={text('lblOrganisation')}
                                    data-testid="input-organisation"
                                    id="deviceCompanyName"
                                    key="device CompanyName"
                                    {...register('companyName')}
                                />
                            </div>
                        </>
                    )}
                </div>

                <div className="flex gap-4">
                    <div className="flex-1">
                        <Controller
                            name="status"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <Dropdown
                                    id="status-dropdown"
                                    data-testid="status-id"
                                    className="w-full"
                                    options={statuses}
                                    placeholder={text('lblAnyStatusOption')}
                                    showClear
                                    onChange={(e) => onChange(e.value)}
                                    value={value}
                                />
                            )}
                        />
                    </div>

                    <div className="flex-1">
                        <Controller
                            name="productId"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <Dropdown
                                    id="measurementDeviceProducts-dropdown"
                                    data-testid="measurementDeviceProducts-id"
                                    className="w-full"
                                    options={measurementDeviceProducts}
                                    placeholder={text('lblAnyProductOption')}
                                    showClear
                                    onChange={(e) => onChange(e.value)}
                                    value={value}
                                />
                            )}
                        />
                    </div>

                    {isSupportDomain && (
                        <>
                            <div className="flex-1">
                                <Input
                                    placeholder={text('lblPartnerField')}
                                    data-testid="input-serial"
                                    id="devicePartner"
                                    key="device Partner"
                                    {...register('partnerName')}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="flex gap-4 justify-content-between align-items-center mt-4">
                <div className="flex gap-4">{actions}</div>
                <div className="flex gap-4">
                    <Button label={text('lblSearch')} type="submit" icon="pi pi-search">
                        {isLoading && <span className="fa fa-spinner fa-pulse" data-testid="btn-search-spinner" />}
                    </Button>

                    <Button
                        type="submit"
                        data-testid="btn-clear"
                        icon="pi pi-times"
                        onClick={() => {
                            clearDevice()
                            reset()
                        }}
                        label={text('btnClear')}
                    />
                </div>
            </div>
        </form>
    )
})
